import { Button, Row, Tag, Tooltip, Typography } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';

import { Comment, Contract } from '../../../../api';
import Table from '../../../../components/general/table';
import { ExportExcelProps } from '../../../../helpers/excel';
import CommentsForm from '../detail';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { CanShow } from '../../../../components';
import { ExportPdfProps } from '../../../../helpers/pdf';
import { useUser } from '../../../../hooks';
import { getFilteredDataFromLocalStorage } from '../../../../helpers/saveFilterData';

const label = 'comentario';
const route = '/comments';
const titlePdf = 'Reporte de comentarios';

type GenerateTableColumnProps = {
  getColumnSearchProps: (propName: string) => ColumnType<Comment>;
  showModal: (type: string, id: number | null) => void;
  handleDelete: (id: number) => void;
};
const as01 = (v: unknown) =>
  v === 1 || v === '1' || v === true ? 1 : 0;

const CommentsList = () => {
  const { Text } = Typography;
  const navigate = useNavigate();
  const { user } = useUser();
  const isSupplierOrCustomer =
    [3, 4].includes((user as any)?.role_id) ||
    ['Supplier', 'Customer'].includes((user as any)?.role_description);
  const data = getFilteredDataFromLocalStorage("comentario");
  const generateTableColumns: (
    props: GenerateTableColumnProps
  ) => ColumnsType<Comment> = (props) => {
    const { getColumnSearchProps, showModal, handleDelete } = props;

    const cols: ColumnsType<Comment> = [
      {
        title: 'Creado por',
        key: 'created_by',
        filteredValue : data?.filters['created_by'] || [],
        ...getColumnSearchProps('created_by'),
        render: (_: any, r: any) => r.customer_name ?? r.supplier_name ?? '—',
        onFilter: (value, r) =>
          [r.customer_name, r.supplier_name]
            .some(n => (n ?? '').toLowerCase().includes(String(value).toLowerCase())),
      },
     {
        title: 'Descripción',
        dataIndex: 'description',
        key: 'description',
        filteredValue : data?.filters['description'] || [],
        ...getColumnSearchProps('description'),
        render: (description?: string) => {
          const txt = typeof description === 'string' ? description : '';
          const needsTrim = txt.length > 100;
          const shown = needsTrim ? `${txt.slice(0, 100)}...` : txt;
          return needsTrim ? (
            <Tooltip title={txt}>
              <Text>{shown}</Text>
            </Tooltip>
          ) : (
            <Text>{shown}</Text>
          );
        },
      },
      {
        title: 'Visible',
        key: 'is_visible',
        dataIndex: 'is_visible',
        filters: [
          { text: 'Visible', value: 1 },
          { text: 'No visible', value: 2 },
        ],
        filteredValue : data?.filters['is_visible'] || [],
        onFilter: (value, record) => as01(record.is_visible) === Number(value),
        sorter: (a, b) => as01(a.is_visible) - as01(b.is_visible),
        sortOrder: data?.sorter?.field === "is_visible" ? data?.sorter.order : null,
        render: (is_visible) => (
          <Row gutter={16} style={{ width: 190 }}>
            <Tag
              color={is_visible === 1 ? 'green' : 'red'}
              key={is_visible}
              style={{ marginBottom: 10 }}
            >
              {is_visible === 1 ? 'Visible' : 'No visible'}
            </Tag>
          </Row>
        ),
      },
      {
        title: 'Fecha de creación',
        dataIndex: 'created_at',
        key: 'created_at',
        filteredValue : [],
        render: (created_at) => (
          <>{moment.unix(created_at).format('DD/MM/YYYY')}</>
        ),
      },
      {
        title: 'Fecha de modificación',
        dataIndex: 'updated_at',
        key: 'updated_at',
        filteredValue : [],
        render: (created_at) => (
          <>{moment.unix(created_at).format('DD/MM/YYYY')}</>
        ),
      },
      {
        title: 'Acciones',
        key: 'sup',
        fixed: 'right',
        width: 170,
        align: 'center',
        filteredValue : [],
        render: (contract, record) => (
          <>
            <>
              <Tooltip title="Editar">
                <Button
                  style={{
                    marginLeft: 10,
                    backgroundColor: '#1890ff',
                    borderColor: '#1890ff',
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => showModal('edit', contract.id)}
                />
              </Tooltip>
            </>
            <Tooltip title="Eliminar">
              <Button
                style={{ marginLeft: 10 }}
                danger
                type="primary"
                shape="default"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(contract.id)}
              />
            </Tooltip>
          </>
        ),
      },
    ];

    return isSupplierOrCustomer
      ? cols.filter(c => !['created_by', 'is_visible'].includes(String(c.key)))
      : cols;
  };

  const generateExportExcelProps: () => ExportExcelProps = () => ({
  fileName: 'Reporte_Comentarios',
  sheetName: 'Comentarios',
  tableName: 'Tabla de comentarios',
  columnNames: [
    { label: 'Descripción', key: 'description' },
    { label: 'Creado por (cliente)', key: 'customer_name' },
    { label: 'Creado por (proveedor)', key: 'supplier_name' },
  ],
});

const pdfColumns: ExportPdfProps[] = [
  { header: 'Descripción', key: 'description' },
  { header: 'Creado por (cliente)', key: 'customer_name' },
  { header: 'Creado por (proveedor)', key: 'supplier_name' },
];

  const commentsTableProps = {
    label,
    route,
    generateTableColumns,
    generateExportExcelProps,
    detailForm: CommentsForm,
    pdfColumns,
    titlePdf,
  };
  const CommentsTable = Table<Contract>(commentsTableProps);

  return CommentsTable;
};

export default CommentsList;
